import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { Reveal } from "../components/motion/Reveal";
import { TextReveal } from "../components/motion/TextReveal";
import { Overline } from "../components/blocks";
import { useRef } from "react";

export default function CommodityDetail() {
  const { slug } = useParams();
  const { content } = useLanguage();
  const item = content.commodities.find((c) => c.slug === slug);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  if (!item) return <Navigate to="/matieres-premieres" replace />;

  const specs = [
    { label: "Origine", value: item.origin },
    { label: "Qualité", value: item.quality },
    { label: "Utilisation", value: item.use },
    { label: "Marchés", value: item.markets },
    { label: "Exportation", value: item.export },
  ];

  return (
    <div data-testid={`commodity-detail-${slug}`}>
      <section ref={heroRef} className="relative h-[70vh] overflow-hidden">
        <motion.div style={{ y }} className="absolute inset-0 -bottom-20">
          <img src={item.image} alt={item.name} className="h-full w-full object-cover" loading="eager" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/40" />
        <div className="relative z-10 mx-auto flex h-full max-w-[1400px] flex-col justify-end px-6 pb-16 md:px-12 md:pb-24">
          <Reveal variant="fade" className="mb-4">
            <Overline>{item.tagline}</Overline>
          </Reveal>
          <h1 className="font-serif text-5xl font-light tracking-tight text-white md:text-7xl">
            <TextReveal text={item.name} />
          </h1>
        </div>
      </section>

      <section className="bg-brand-beige py-20 md:py-28">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-16 px-6 md:grid-cols-12 md:px-12">
          {/* sticky image */}
          <div className="md:col-span-5">
            <div className="md:sticky md:top-28">
              <Reveal variant="left" className="overflow-hidden">
                <img src={item.image} alt={item.name} className="aspect-[4/5] w-full object-cover" loading="lazy" />
              </Reveal>
              <Link
                to="/matieres-premieres"
                data-testid="back-to-commodities"
                className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-brand-green transition-colors hover:text-brand-gold"
              >
                <ArrowLeft className="h-4 w-4" />
                {content.ui.buttons.backToCommodities}
              </Link>
            </div>
          </div>

          {/* scrolling details */}
          <div className="md:col-span-7">
            <Reveal variant="up" className="mb-12 text-xl font-light leading-relaxed text-brand-black/80 md:text-2xl">
              {item.description}
            </Reveal>
            <div className="space-y-0">
              {specs.map((sp, i) => (
                <Reveal
                  key={i}
                  variant="up"
                  delay={i * 0.06}
                  className="grid grid-cols-1 gap-2 border-t border-brand-green/15 py-8 md:grid-cols-3 md:gap-8"
                >
                  <p className="text-xs uppercase tracking-[0.2em] text-brand-gold">{sp.label}</p>
                  <p className="text-base leading-relaxed text-brand-black/75 md:col-span-2">{sp.value}</p>
                </Reveal>
              ))}
            </div>
            <Reveal variant="up" className="mt-14">
              <Link
                to="/contact"
                data-testid="commodity-contact-btn"
                className="inline-block rounded-none bg-brand-green px-10 py-5 text-xs uppercase tracking-[0.15em] text-white transition-colors hover:bg-brand-black"
              >
                {content.ui.nav.contact}
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
